import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from '@/redux/store'
import WebSocketManager from '@/services/websocketManager'
import {
  recordRatificationVote,
  setRatificationFinalizing,
  setGroupLoadingMore,
} from '../slices/charterGroupWsSlice'

const wsManager = WebSocketManager.getInstance()

// ──────────────────────────────────────────────────────────
// Phase 1 — Prep Coach WebSocket
// ──────────────────────────────────────────────────────────

export const connectCharterPrepWS = createAsyncThunk<
  void,
  { jwtToken: string; roomId: string; inviteCode: string; teamId?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/connectCharterPrep',
  async ({ jwtToken, roomId, inviteCode, teamId }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToCharterPrep(jwtToken, roomId, inviteCode, teamId)
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to connect to charter prep WebSocket')
    }
  }
)

export const disconnectCharterPrepWS = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('ws/disconnectCharterPrep', async () => {
  return await wsManager.disconnect('charter_prep')
})

export const sendPrepMessage = createAsyncThunk<
  void,
  { message: string; section_key?: string },
  { dispatch: AppDispatch; state: RootState }
>('ws/sendPrepMessage', async ({ message, section_key }, { rejectWithValue }) => {
  const success = wsManager.sendMessage('charter_prep', {
    type: 'SEND_MESSAGE',
    message,
    section_key,
  })
  if (!success) {
    return rejectWithValue('Charter prep WebSocket is not connected')
  }
})

export const setSharingSectionMode = createAsyncThunk<
  void,
  { sharing_mode: string },
  { dispatch: AppDispatch; state: RootState }
>('ws/setPrepSharingMode', async ({ sharing_mode }, { rejectWithValue }) => {
  const success = wsManager.sendMessage('charter_prep', {
    type: 'SET_SHARING_MODE',
    sharing_mode,
  })
  if (!success) {
    return rejectWithValue('Charter prep WebSocket is not connected')
  }
})

export const setPrepVisibleSections = createAsyncThunk<
  void,
  { visible_sections: string[] },
  { dispatch: AppDispatch; state: RootState }
>('ws/setPrepVisibleSections', async ({ visible_sections }, { rejectWithValue }) => {
  const success = wsManager.sendMessage('charter_prep', {
    type: 'SET_VISIBLE_SECTIONS',
    visible_sections,
  })
  if (!success) {
    return rejectWithValue('Charter prep WebSocket is not connected')
  }
})

export const approvePrepProfile = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('ws/approvePrepProfile', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('charter_prep', {
    type: 'APPROVE_PROFILE',
  })
  if (!success) {
    return rejectWithValue('Charter prep WebSocket is not connected')
  }
})

// ──────────────────────────────────────────────────────────
// Phase 2 & 3 — Group Moderator WebSocket
// ──────────────────────────────────────────────────────────

export const connectCharterGroupWS = createAsyncThunk<
  void,
  { jwtToken: string; roomId: string; inviteCode: string; teamId: string; monitor?: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/connectCharterGroup',
  async ({ jwtToken, roomId, inviteCode, teamId, monitor }, { rejectWithValue }) => {
    try {
      return await wsManager.connectToCharterGroup(jwtToken, roomId, inviteCode, teamId, monitor)
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || 'Failed to connect to charter group WebSocket'
      )
    }
  }
)

export const disconnectCharterGroupWS = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('ws/disconnectCharterGroup', async () => {
  return await wsManager.disconnect('charter_group')
})

export const sendGroupMessage = createAsyncThunk<
  void,
  { content: string; mentionsModerator?: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/sendGroupMessage',
  async ({ content, mentionsModerator = false }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('charter_group', {
      type: 'SEND_GROUP_MESSAGE',
      content,
      // #573: an explicit summon always earns a reply. Sent as a flag rather
      // than inferred from the text so the moderator's decision never depends
      // on parsing chat content.
      mentions_moderator: mentionsModerator,
    })
    if (!success) {
      return rejectWithValue('Charter group WebSocket is not connected')
    }
  }
)

export const loadMoreGroupMessages = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/loadMoreGroupMessages',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { pagination, isLoadingMore } = getState().charterGroupWS
    if (isLoadingMore || !pagination?.has_more) return

    dispatch(setGroupLoadingMore(true))
    const success = wsManager.sendMessage('charter_group', {
      type: 'LOAD_MORE_MESSAGES',
      before_id: pagination.oldest_loaded_id,
    })
    if (!success) {
      dispatch(setGroupLoadingMore(false))
      return rejectWithValue('Charter group WebSocket is not connected')
    }
  }
)

export const confirmSectionConsensus = createAsyncThunk<
  void,
  { section_key: string; consensus_text: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/confirmSectionConsensus',
  async ({ section_key, consensus_text }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('charter_group', {
      type: 'CONFIRM_SECTION_CONSENSUS',
      section_key,
      consensus_text,
    })
    if (!success) {
      return rejectWithValue('Charter group WebSocket is not connected')
    }
  }
)

export const sendRatificationVote = createAsyncThunk<
  void,
  { vote: boolean; section_key?: string; concern?: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'ws/sendRatificationVote',
  async ({ vote, section_key, concern }, { getState, dispatch, rejectWithValue }) => {
    const success = wsManager.sendMessage('charter_group', {
      type: 'RATIFICATION_VOTE',
      vote,
      section_key,
      concern,
    })
    if (!success) {
      return rejectWithValue('Charter group WebSocket is not connected')
    }

    // Optimistic local update — reflect the acting voter's own vote immediately
    // instead of waiting for the server `ratification_update` echo. The server
    // remains authoritative: its echo overwrites this via the same reducer.
    const state = getState()
    const profile = state.charter.myProfile
    const memberId = String(profile?.member_id ?? '')
    if (!memberId) return // no member identity (e.g. observer) → nothing to predict

    const { ratificationVotes, totalMembers } = state.charterGroupWS
    const optimisticVotes = {
      ...ratificationVotes,
      [memberId]: {
        vote,
        voted_at: new Date().toISOString(),
        member_name: profile?.member_name,
        ...(section_key ? { section_key } : {}),
        ...(concern ? { concern } : {}),
      },
    }
    const yesCount = Object.values(optimisticVotes).filter((v) => v.vote).length

    dispatch(
      recordRatificationVote({
        votes: optimisticVotes,
        total_members: totalMembers, // keep server-known total; never fabricate
        yes_count: yesCount,
      })
    )

    // Only the LAST unanimous YES vote may optimistically show the in-progress
    // banner. Gate strictly on the server-known `totalMembers` (> 0) so an early
    // voter — when totalMembers is still 0 — can never falsely trigger it.
    if (vote && totalMembers > 0 && yesCount === totalMembers) {
      dispatch(setRatificationFinalizing(true))
    }
  }
)

// cancelRenegotiation moved to an HTTP thunk in charterThunks.ts (the WS path
// was subject to the consumer's serialized dispatch loop and could lag behind
// moderator streaming).

